import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { LessonComment, NewLessonComment } from '../types/comments';
import { firestoreWrapper } from './firestoreWrapper';

// Collection name
const COMMENTS_COLLECTION = 'ai-devquest-lesson-comments';

/**
 * Service for managing lesson comments
 */
export const commentsService = {
  /**
   * Get all comments for a lesson
   */
  getLessonComments: async (lessonId: string): Promise<LessonComment[]> => {
    try {
      const q = query(
        collection(db, COMMENTS_COLLECTION),
        where('lessonId', '==', lessonId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await firestoreWrapper.getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LessonComment[];
    } catch (error) {
      
      throw error;
    }
  },

  /**
   * Get comment count for a lesson
   */
  getLessonCommentCount: async (lessonId: string): Promise<number> => {
    try {
      const q = query(
        collection(db, COMMENTS_COLLECTION),
        where('lessonId', '==', lessonId)
      );
      
      const querySnapshot = await firestoreWrapper.getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      
      throw error;
    }
  },

  /**
   * Add a new comment
   */
  addComment: async (comment: NewLessonComment): Promise<string> => {
    try {
      const docRef = await firestoreWrapper.addDoc(collection(db, COMMENTS_COLLECTION), {
        ...comment,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      
      throw error;
    }
  },

  /**
   * Get all comments counts for multiple lessons
   */
  /**
   * Update a comment
   */
  updateComment: async (commentId: string, content: string): Promise<void> => {
    try {
      const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
      await firestoreWrapper.updateDoc(commentRef, {
        content,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      
      throw error;
    }
  },

  /**
   * Delete a comment
   */
  deleteComment: async (commentId: string): Promise<void> => {
    try {
      const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
      await firestoreWrapper.deleteDoc(commentRef);
    } catch (error) {
      
      throw error;
    }
  },

  getLessonCommentCounts: async (lessonIds: string[]): Promise<Record<string, number>> => {
    try {
      const q = query(
        collection(db, COMMENTS_COLLECTION),
        where('lessonId', 'in', lessonIds)
      );
      
      const querySnapshot = await firestoreWrapper.getDocs(q);
      const counts: Record<string, number> = {};
      
      // Initialize counts
      lessonIds.forEach(id => counts[id] = 0);
      
      // Count comments for each lesson
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.lessonId) {
          counts[data.lessonId] = (counts[data.lessonId] || 0) + 1;
        }
      });
      
      return counts;
    } catch (error) {
      
      throw error;
    }
  }
};